import { useEffect, useState } from "react";
import {
  DialogContent,
  Button,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import { ReceiptLongOutlined, ReceiptOutlined } from "@mui/icons-material";
import {
  MoleCancelButton,
  MoleDialog,
  MoleDialogActions,
  MoleDialogHeader,
  MolePrimaryButton,
  useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHashtag,
  faCalendarAlt,
  faUser,
  faPerson,
  faFilePdf,
  faWallet,
  faMoneyBill,
  faPrint,
  faTruck,
  faUserTie,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../hooks/useAuth";

import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { Util } from "../../util/Util";
import ContactDetailsPanel, { hasContactDetails } from "../client/ContactDetailsPanel";
import { Z_INDEX } from "../../styles/zIndex";
import { useAppSocket } from "../../providers/AppSocketContext";
import { printReceiptPdf, setupQzTray } from "../../util/qzTray";
import { openPdfBase64, ReceiptSource } from "../../util/receiptPdf";
import ReceiptDialog from "../../components/ReceiptDialog";
import DocumentExportDialog, { DocumentExportLayout, DocumentImageSize } from "../../components/DocumentExportDialog";

import { addMoney, roundMoney } from "../../util/quantity";
import { canAmend, canPrintReceipt } from '../../util/access';
interface Props {
  isOpen: boolean;
  toggle: () => void;
  bundle: any;
}

const InvoiceReport = ({ isOpen, toggle, bundle }: Props) => {
  const [record, setRecord] = useState<any>(null);
  const [receiptSource, setReceiptSource] = useState<ReceiptSource | null>(null);
  const [exportTarget, setExportTarget] = useState<"invoice" | "delivery" | null>(null);
  const { api, access } = useAuth();
  const { queueReRun, t, setLoadingMessage } = useUI();
  const { appSettings } = useAppSocket();

  const themeColor = useMoleThemeColor(appSettings?.UI_THEME_PRIMARY?.value);

  const fetchData = async () => {
    const res = await api.get(`sales/${bundle.batch}`);
    if (res) {
      if (res.status === 200) {
        setRecord(res.data);
        return;
      } else if (res.status === 401 || res.status === 422) {
        queueReRun(fetchData);
      } else if (res.status === 404) {
        toast.warn(t("common:toast.warn.noRecord"));
      } else {
        console.error("Failed to fetch data:", res);
      }
    }
    toggle();
  };

  useEffect(() => {
    if (!record) return;
    return setupQzTray();
  }, [record]);

  const handlePrint = () => {
    void printReceiptPdf(api, bundle.batch, setLoadingMessage);
  };

  const handleExport = (layout: DocumentExportLayout, imageSize: DocumentImageSize) => {
    const params = new URLSearchParams();
    if (layout === "images") {
      params.set("with_images", "1");
      params.set("image_size", imageSize);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const suffix = exportTarget === "delivery" ? "/delivery_note" : "";
    exportPDF(`/reports/sales/invoice/${bundle.batch}${suffix}${query}`);
  };

  const exportPDF = async (path: string) => {
    setLoadingMessage("EXPORT");

    const res = await api.get(path);

    if (res) {
      if (res.status === 200 && res.data?.data) {
        openPdfBase64(res.data.data);
      } else {
        console.error("Failed to fetch data:", res);
        toast.error(t("common:report.failedToGenerate"));
      }
    }

    setLoadingMessage(null);
  };

  useEffect(() => {
    if (isOpen && bundle?.batch) fetchData();
  }, [isOpen, bundle?.batch]);

  const subtotal = record?.records?.reduce((acc: number, r: any) => addMoney(acc, (r?.total || 0)), 0) || 0;
  const taxTotal = record?.records?.reduce((acc: number, r: any) => addMoney(acc, (r?.tax_amount || 0)), 0) || 0;
  const grandTotal = record?.details?.total || addMoney(subtotal, taxTotal) || 0;
  const amountPaid = record?.payments?.reduce((acc: number, p: any) => addMoney(acc, (p.amount || 0)), 0) || 0;
  const balanceDue = roundMoney(grandTotal - amountPaid);

  return (
    <MoleDialog open={isOpen} onClose={toggle} maxWidth="md" fullWidth themeColor={themeColor} paperOptions={{ maxHeight: "95vh" }} sx={{ zIndex: Z_INDEX.nestedDialog }}>
      <MoleDialogHeader
        eyebrow={record?.details?.is_return ? t("common:report.returnInvoice") : t("common:report.salesInvoice")}
        title={record?.details?.reference || bundle.reference}
        subtitle={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{t("common:ui.labels.status")}</Typography>
            <Chip
              label={balanceDue === grandTotal ? t("common:status.unpaid") : balanceDue === 0 ? t("common:status.fullyPaid") : t("common:status.partialPayment")}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: balanceDue === 0 ? "success.main" : balanceDue === grandTotal ? "error.main" : "warning.main",
                color: balanceDue === 0 ? "success.contrastText" : balanceDue === grandTotal ? "error.contrastText" : "warning.contrastText",
              }}
            />
          </Stack>
        }
        icon={<ReceiptLongOutlined />}
        onClose={toggle}
        themeColor={themeColor}
      />

      <DialogContent sx={{ p: 4 }}>
        {record ? (
          <Box>
            {/* Header Info Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 800 }}>{t("common:report.invoiceDetails")}</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: "background.default", borderStyle: "dashed" }}>
                  <Stack spacing={1.5}>
                    <DetailItem icon={faHashtag} label={t("common:report.invoiceNo")} value={record?.details.reference} />
                    {Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_AGENTS?.value) && <DetailItem icon={faUserTie} label={t("common:report.agent")} value={record?.details?.agent} />}
                    <DetailItem icon={faPerson} label={t("common:report.client")} value={record?.details?.client} />
                    <DetailItem
                      icon={faStore}
                      label={t("common:tables.pos")}
                      value={
                        (record?.details?.is_return
                          ? record?.details?.destination
                          : record?.details?.source) || ""
                      }
                    />
                    <DetailItem icon={faCalendarAlt} label={t("common:report.billingDate")} value={Util.formatDate(record?.details?.date, "full")} />
                    <DetailItem icon={faUser} label={t("common:report.invoiceBy")} value={record?.details?.user} />
                  </Stack>
                </Paper>
              </Grid>
              {hasContactDetails(record?.details) && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ContactDetailsPanel details={record?.details} />
                </Grid>
              )}
            </Grid>

            {/* Items Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 4 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 2 }}>{t("common:report.description")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.quantity")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.unitPrice")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.total")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.records?.map((item: any, idx: number) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                        {Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value) && <Typography variant="caption" color="text.secondary">{t("common:report.code", { code: item.item_code || t("common:ui.placeholders.notAvailable") })}</Typography>}
                      </TableCell>
                      <TableCell align="right">{Util.formatNumber(item.quantity)}</TableCell>
                      <TableCell align="right">{Util.formatNumber(item.unit_price || item.amount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{Util.formatNumber(item.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Bottom Section: Payments & Totals */}
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <FontAwesomeIcon icon={faWallet} style={{ color: themeColor }} /> {t("common:report.paymentBreakdown")}
                </Typography>
                <Stack spacing={1}>
                  {record.payments?.map((payment: any, idx: number) => (
                    <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, borderRadius: 2, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ color: "text.secondary" }}>
                          <FontAwesomeIcon icon={payment.mode.toLowerCase() === "cash" ? faMoneyBill : faWallet} size="sm" />
                        </Box>
                        <Typography variant="body2" fontWeight={600}>{payment.mode}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700}>{Util.formatNumber(payment.amount)}</Typography>
                    </Box>
                  ))}
                  {(!record.payments || record.payments.length === 0) && (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">{t("common:report.noPaymentRecords")}</Typography>
                  )}
                </Stack>

                {record.details?.notes && (
                  <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: "action.hover", borderLeft: "4px solid", borderColor: "info.main" }}>
                    <Typography variant="caption" fontWeight={800} color="info.main" sx={{ textTransform: "uppercase" }}>{t("common:taskPane.notes")}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{record.details.notes}</Typography>
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: "grey.900", color: "white" }}>
                  <Stack spacing={1.5}>
                    <SummaryRow label={t("common:report.subtotal")} value={Util.formatNumber(subtotal)} />
                    <SummaryRow label={t("common:report.totalTax")} value={Util.formatNumber(taxTotal)} />
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight={400}>{t("common:report.grandTotal")}</Typography>
                      <Typography variant="h5" fontWeight={900} sx={{ color: themeColor }}>{Util.formatNumber(grandTotal)}</Typography>
                    </Stack>
                    <SummaryRow label={t("common:report.totalPaid")} value={Util.formatNumber(amountPaid)} opacity={0.7} />
                    {balanceDue > 0 && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: "rgba(255, 152, 0, 0.1)", borderRadius: 1, display: "flex", justifyContent: "space-between", color: "warning.main" }}>
                        <Typography variant="body2" fontWeight={700}>{t("common:report.balanceDue")}</Typography>
                        <Typography variant="body2" fontWeight={800}>{Util.formatNumber(balanceDue)}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
            <CircularProgress thickness={5} size={48} sx={{ color: themeColor }} />
            <Typography sx={{ mt: 2, color: "text.secondary" }}>{t("common:report.fetchingDetails")}</Typography>
          </Stack>
        )}
      </DialogContent>

      <MoleDialogActions>
        <MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
        <MolePrimaryButton
          startIcon={<FontAwesomeIcon icon={faPrint} />}
          onClick={handlePrint}
          disabled={!record}
        >
          {t("common:ui.buttons.printInvoice")}
        </MolePrimaryButton>
        {!record?.details?.is_return && record?.payments?.length > 0 && canPrintReceipt(access) && (
          <MolePrimaryButton
            startIcon={<ReceiptOutlined />}
            onClick={() => setReceiptSource({ kind: "invoice", invoice: bundle.batch, reference: record?.details?.reference })}
          >
            {t("common:receipt.paymentTitle")}
          </MolePrimaryButton>
        )}
        <MolePrimaryButton
          startIcon={<FontAwesomeIcon icon={faTruck} />}
          onClick={() => setExportTarget("delivery")}
          disabled={!record}
        >
          {t("common:ui.buttons.printDeliveryNote")}
        </MolePrimaryButton>
        <MolePrimaryButton
          startIcon={<FontAwesomeIcon icon={faFilePdf} />}
          onClick={() => setExportTarget("invoice")}
          disabled={!record}
        >
          {t("common:ui.buttons.exportAsPdf")}
        </MolePrimaryButton>
      </MoleDialogActions>
      <ReceiptDialog source={receiptSource} onClose={() => setReceiptSource(null)} />
      <DocumentExportDialog
        open={exportTarget !== null}
        onClose={() => setExportTarget(null)}
        onExport={handleExport}
        document={bundle?.batch ? { kind: "sale", batch: bundle.batch } : undefined}
        canEditImages={canAmend(access, "sale")}
      />
    </MoleDialog>
  );
};

const SummaryRow = ({ label, value, opacity = 1 }: { label: string; value: string; opacity?: number }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ opacity }}>
    <Typography variant="body2">{label}</Typography>
    <Typography variant="body2" fontWeight={600}>{value}</Typography>
  </Stack>
);

const DetailItem = ({ icon, label, value }: { icon: any, label: string, value: string }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <FontAwesomeIcon icon={icon} style={{ width: 14, color: "#64748b" }} />
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
    <Typography variant="body2" fontWeight={600}>{value || "—"}</Typography>
  </Stack>
);

export default InvoiceReport;